import type {ReactElement} from 'react';
import React, {useEffect} from 'react';
import {Text, TextSize} from '@Components/text';
import {Button, Size, Type} from '@Components/button';
import {setIsTextMaskEditingPanelDisabled} from '@Panels/masking-panel/components/text-masking-panel/text-masking-panel-reducer';
import type {TextStylesObject} from '@PosterWhiteboard/classes/text-styles.class';
import {TextHorizontalAlignType} from '@PosterWhiteboard/classes/text-styles.class';
import {MAX_FONT_SIZE, MAX_LEADING, MAX_LETTER_SPACING, MIN_FONT_SIZE, MIN_LEADING, MIN_LETTER_SPACING} from '@PosterWhiteboard/classes/masking/masking-text.class';
import {useIsMobileWidth} from '@Hooks/useIsMobileWidth';
import {TextMaskingDropdownOption} from '@Panels/masking-panel/components/text-masking-dropdown-option';
import {ContextualMenu} from '@Components/contextual-menu';
import {ContextualMenuHeadingOption} from '@Components/contextual-menu/components/contextual-menu-heading-option';
import {ContextualMenuSliderOptions} from '@Components/contextual-menu/components/contextual-menu-slider-options';
import {ContextualMenuStylingOption} from '@Components/contextual-menu/components/contextual-menu-styling-option';
import type {IconStates} from '@Components/contextual-menu/components/styling-options';
import {TextStyleOptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-styling-options/base-styling-options.types';
import {useAppDispatch, useAppSelector} from '@/hooks';
import styles from './text-masking-editing-panel.module.scss';

export interface TextMaskingEditingPanelProps {
  isDisabled?: boolean;
}

function TextMaskingEditingPanel({...props}: TextMaskingEditingPanelProps): ReactElement {
  const isDisabled = useAppSelector((state) => {
    return state.textMasking.isEditingPanelDisabled;
  });

  const isMobileWidth = useIsMobileWidth();

  const maskedTextStyles: Partial<TextStylesObject> = useAppSelector((state) => {
    return {
      isBold: state.textMasking.maskedTextProps.isBold,
      isItalic: state.textMasking.maskedTextProps.isItalic,
      underLine: state.textMasking.maskedTextProps.underLine,
      lineThrough: state.textMasking.maskedTextProps.lineThrough,
      fontFamily: state.textMasking.maskedTextProps.fontFamily,
      fontSize: state.textMasking.maskedTextProps.fontSize,
      textAlign: state.textMasking.maskedTextProps.textAlign,
      leading: state.textMasking.maskedTextProps.leading,
      letterSpacing: state.textMasking.maskedTextProps.letterSpacing,
    };
  });

  const dispatch = useAppDispatch();

  const getAlignmentOptionData = (): Array<IconStates> => {
    return [
      {
        id: TextHorizontalAlignType.LEFT,
        icon: 'icon-align-left',
        isSelected: maskedTextStyles.textAlign === TextHorizontalAlignType.LEFT,
      },
      {
        id: TextHorizontalAlignType.CENTER,
        icon: 'icon-align-center',
        isSelected: maskedTextStyles.textAlign === TextHorizontalAlignType.CENTER,
      },
      {
        id: TextHorizontalAlignType.RIGHT,
        icon: 'icon-align-right',
        isSelected: maskedTextStyles.textAlign === TextHorizontalAlignType.RIGHT,
      },
      {
        id: TextHorizontalAlignType.JUSTIFY,
        icon: 'icon-align-block',
        isSelected: maskedTextStyles.textAlign === TextHorizontalAlignType.JUSTIFY,
      },
    ];
  };

  const getStylingOptionData = (): Array<IconStates> => {
    return [
      {
        id: TextStyleOptions.BOLD,
        icon: 'icon-bold',
        isSelected: maskedTextStyles.isBold,
      },
      {
        id: TextStyleOptions.ITALIC,
        icon: 'icon-italic',
        isSelected: maskedTextStyles.isItalic,
      },
      {
        id: TextStyleOptions.UNDERLINE,
        icon: 'icon-underline',
        isSelected: maskedTextStyles.underLine,
      },
      {
        id: TextStyleOptions.STRIKE_THROUGH,
        icon: 'icon-strike-through',
        isSelected: maskedTextStyles.lineThrough,
      },
    ];
  };

  const onClickAlignmentOptions = (id: TextHorizontalAlignType): void => {
    if (!window.textMask) {
      return;
    }
    if (maskedTextStyles.textAlign === id) {
      return;
    }
    window.textMask.setAlignment(id);
  };

  const onClickStylingOptions = (id: TextStyleOptions): void => {
    if (!window.textMask) {
      return;
    }
    switch (id) {
      case TextStyleOptions.UNDERLINE:
        window.textMask.setUnderline(!maskedTextStyles.underLine);
        break;
      case TextStyleOptions.STRIKE_THROUGH:
        window.textMask.setLineThrough(!maskedTextStyles.lineThrough);
        break;
      case TextStyleOptions.ITALIC:
        window.textMask.setIsItalic(!maskedTextStyles.isItalic);
        break;
      case TextStyleOptions.BOLD:
        window.textMask.setIsBold(!maskedTextStyles.isBold);
        break;
      default:
        throw new Error(`unhandled styling option for text masking`);
    }
  };

  const getEditingOptionsPanel = (): ReactElement => {
    return (
      <ContextualMenu className={isDisabled ? styles.overlayBlur : ''} includeLeftRightPadding={false} id="text-masking-options" contentClassName="spacing-p-t-0 spacing-p-r-6">
        <ContextualMenuHeadingOption id="select-font-option" text={window.i18next.t('pmwjs_select_a_font')} />
        <TextMaskingDropdownOption />
        <ContextualMenuHeadingOption id="select-font-style" text={window.i18next.t('pmwjs_font_style')} />
        <ContextualMenuSliderOptions
          id="masking-font-size-option"
          value={maskedTextStyles.fontSize ?? 0}
          label={window.i18next.t('pmwjs_size')}
          range={{min: MIN_FONT_SIZE, max: MAX_FONT_SIZE}}
          transformDisplay={{min: MIN_FONT_SIZE, max: MAX_FONT_SIZE}}
          onChange={(val: number): void => {
            if (window.textMask) {
              window.textMask.setFontSize(val);
            }
          }}
        />
        <ContextualMenuStylingOption id="masking-text-style-option" label={window.i18next.t('pmwjs_styles')} data={getStylingOptionData()} onClick={onClickStylingOptions} />
        <ContextualMenuStylingOption
          id="masking-text-alignment-option"
          label={window.i18next.t('pmwjs_alignment')}
          data={getAlignmentOptionData()}
          onClick={onClickAlignmentOptions}
        />
        <ContextualMenuSliderOptions
          id="masking-font-leading-option"
          value={maskedTextStyles.leading ?? 120}
          label={window.i18next.t('pmwjs_line_height')}
          range={{min: MIN_LEADING, max: MAX_LEADING}}
          transformDisplay={{min: MIN_LEADING, max: MAX_LEADING}}
          onChange={(val: number): void => {
            if (window.textMask) {
              window.textMask.setLeading(val);
            }
          }}
        />
        <ContextualMenuSliderOptions
          id="masking-font-size-option"
          value={maskedTextStyles.letterSpacing ?? 0}
          label={window.i18next.t('pmwjs_letter_spacing')}
          range={{min: MIN_LETTER_SPACING, max: MAX_LETTER_SPACING}}
          transformDisplay={{min: MIN_LETTER_SPACING, max: MAX_LETTER_SPACING}}
          onChange={(val: number): void => {
            if (window.textMask) {
              window.textMask.setLetterSpacing(val);
            }
          }}
        />
      </ContextualMenu>
    );
  };

  const onAddTextMaskClicked = (): void => {
    void window.textMask?.restoreMask();
  };

  const getDisabledStateOverlay = (): ReactElement => {
    return (
      <div className={`${styles.overlayContainer} _full-width _full-height`}>
        <div className={`${styles.diabledOverlayInnerContainer} flex-center flex-v-row`}>
          <Button type={Type.PRIMARY} icon="icon-plus" size={Size.SMALL} text="Add Text" onClick={onAddTextMaskClicked} />
          <Text val="Click to add Text Mask on this photo" size={TextSize.XXSMALL} />
        </div>
        <div className={`${styles.overlay} _full-width _full-height flex-center`} />
      </div>
    );
  };

  const getPanel = (): ReactElement => {
    return isDisabled ? (
      <div className={styles.panelContainer}>
        {getEditingOptionsPanel()}
        {getDisabledStateOverlay()}
      </div>
    ) : (
      getEditingOptionsPanel()
    );
  };

  useEffect(() => {
    if (props.isDisabled) {
      dispatch(setIsTextMaskEditingPanelDisabled(true));
    }
  }, []);

  return <div className={`_full-height ${styles.editingPanel} ${isMobileWidth ? '_full-width' : styles.desktopWidth}`}>{getPanel()}</div>;
}

export default TextMaskingEditingPanel;
